import {
  Box,
  chakra,
  Flex,
  Image,
  shouldForwardProp,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { isValidMotionProp, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import TermsModal from "./TermsModal";
import { IoMdInformationCircleOutline } from "react-icons/io";
import useWebsiteInfo from "../../hook/useWebsiteInfo";
import { COMMON_WEB_PATH } from "../../constant";

const ChakraBox = chakra(motion.div, {
  shouldForwardProp: (prop) =>
    isValidMotionProp(prop) || shouldForwardProp(prop),
});

const AgeCheckModal = () => {
  const { t } = useTranslation();
  const i18n = (key) => t(`common.${key}`);
  const i18nTerms = (key) => t(`Terms And Service.${key}`);
  const i18nPrivacy = (key) => t(`Privacy Policy.${key}`);

  const [isChecked, setIsChecked] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showTerms, setShowTerms] = useState(false); // 服務條款
  const [showPrivacy, setShowPrivacy] = useState(false); // 隱私政策

  const navigate = useNavigate();

  // 取網站資訊 - 直接獲取預處理好的圖片路徑
  const { images, isGD88 } = useWebsiteInfo();

  // 根據網站類型決定文字顏色
  const checkboxTextColor = isGD88 ? "#FFFFFF" : "#FC97FF";

  // 處理確認按鈕
  const handleConfirm = () => {
    if (isChecked) {
      // 如果勾選了 不再顯示
      const expiryDate = dayjs().add(3, "day"); //三天後會過期

      localStorage.setItem(
        "age-verification-expiry-date",
        JSON.stringify({ expiry: expiryDate })
      ); // 存到 localStorage
    }
    setShowModal(false);
  };

  const toggleCheck = () => {
    setIsChecked(!isChecked);
  };

  // 服務條款點擊
  const handleTermsClick = () => {
    setShowTerms(true);
  };

  // 關閉服務條款
  const handleCloseTerms = () => {
    setShowTerms(false);
  };

  // 隱私政策點擊
  const handlePrivacyClick = () => {
    setShowPrivacy(true);
  };
  // 關閉隱私政策
  const handleClosePrivacy = () => {
    setShowPrivacy(false);
  };

  useEffect(() => {
    //取得local的年齡過期日期，如果沒有存在的話代表沒按過，預設值給予他null
    const verification_expiry_date =
      localStorage.getItem("age-verification-expiry-date") || null;
    const pare_verification_expiry_date = verification_expiry_date
      ? JSON.parse(verification_expiry_date)
      : null;

    //將格式轉為 YYYY-MM-DD HH:mm:ss  ex:2022-09-01 00:00:00
    const fmt_verification_expiry_date = pare_verification_expiry_date
      ? dayjs(pare_verification_expiry_date).format("YYYY-MM-DD HH:mm:ss")
      : null;
    const fmt_current_time = dayjs().format("YYYY-MM-DD HH:mm:ss");

    //判斷當前日期是否超過過期日期
    const current_date_is_over_expiry_date = fmt_verification_expiry_date
      ? fmt_verification_expiry_date < fmt_current_time
      : false;

    if (!verification_expiry_date) {
      //Null就顯示Age Modal
      setShowModal(true);
    } else {
      if (current_date_is_over_expiry_date) {
        //過期就顯示Age Modal
        setShowModal(true);
      } else {
        //沒過期就不顯示Age Modal
        setShowModal(false);
      }
    }
  }, []);

  return (
    <>
      {showModal && (
        <Flex
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="rgba(0, 0, 0, 0.8)"
          zIndex="9999"
          backdropFilter="blur(2px)"
          justifyContent="center"
          alignItems="center"
        >
          <ChakraBox
            as={Flex}
            w="520px"
            h="auto"
            bgImage={`url('${images.hintBG}')`}
            bgPosition="center"
            bgRepeat="no-repeat"
            bgSize="100% 100%"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-between"
            p={6}
            // 根據狀態使用不同的動畫
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
            transition={{ duration: 0.3 }}
          >
            <Flex
              fontSize="24px"
              color="white"
              textAlign="center"
              mt={4}
              bgImage={`url('${images.hintTitleBG}')`}
              bgPosition="center"
              bgRepeat="no-repeat"
              bgSize="100% 100%"
              sx={{ width: "-webkit-fill-available" }}
              justifyContent="center"
              alignItems="center"
            >
              <Image
                src={images.hintWORD}
                height={{
                  base: "50px",
                  xl: "60px",
                }}
              />
            </Flex>
            <Image
              src={`${COMMON_WEB_PATH}/FlyDragon/R18_logo.webp`}
              width={{
                base: "120px",
                xl: "165px",
              }}
              my={4}
            />

            <Box fontSize="22px" color="white" textAlign="center" mb={1}>
              <Text color="#FFF" fontWeight="700">
                {i18n("18Age")}
              </Text>
            </Box>

            <Flex gap={4} my={6} justifyContent="center" alignItems="center">
              <Flex
                cursor={"pointer"}
                justifyContent="center"
                alignItems="center"
                onClick={handleConfirm}
                _hover={{
                  filter: "brightness(1.1)",
                }}
              >
                <Image
                  src={images.over18BTN}
                  width={{
                    base: "140px",
                    xl: "165px",
                  }}
                />
              </Flex>
              <Flex
                as="button"
                cursor="pointer"
                justifyContent="center"
                alignItems="center"
                _hover={{ filter: "brightness(1.1)" }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                <Image
                  src={images.under18BTN}
                  width={{
                    base: "140px",
                    xl: "165px",
                  }}
                />
              </Flex>
            </Flex>
            <Flex
              gap={1}
              alignItems="center"
              justifyContent="center"
              cursor="pointer"
              onClick={toggleCheck}
            >
              <Box position="relative">
                <Image
                  src={images.clickBoxBG}
                  width={{
                    base: "35px",
                    xl: "40px",
                  }}
                />
                {isChecked && (
                  <Image
                    src={`${COMMON_WEB_PATH}/FlyDragon/greenCheck.svg`}
                    width={{
                      base: "35px",
                      xl: "40px",
                    }}
                    position="absolute"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                  />
                )}
              </Box>
              <Text color={checkboxTextColor} fontWeight="700">
                {i18n("noShow")}
              </Text>
            </Flex>
            <Flex
              gap={2}
              my={3}
              fontSize="14px"
              //flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Flex
                gap={1}
                alignItems="center"
                cursor="pointer"
                onClick={handleTermsClick}
                _hover={{ color: "#e8c3ff" }}
                color="white"
              >
                <IoMdInformationCircleOutline size={20} />
                <Text>{i18nTerms("Terms of Service")}</Text>
              </Flex>
              <Flex
                gap={1}
                alignItems="center"
                cursor="pointer"
                onClick={handlePrivacyClick}
                _hover={{ color: "#e8c3ff" }}
                color="white"
              >
                <IoMdInformationCircleOutline size={20} />
                <Text>{i18nPrivacy("Privacy Policy")}</Text>
              </Flex>
            </Flex>
          </ChakraBox>
        </Flex>
      )}

      {showTerms && <TermsModal onClose={handleCloseTerms} tabIndex={0} />}
      {showPrivacy && <TermsModal onClose={handleClosePrivacy} tabIndex={1} />}
    </>
  );
};

export default AgeCheckModal;
